import axios from 'axios'

console.log('[api.ts] MODULE LOADED')

// For subdomain deployment, all API calls go through /api/
export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds for AI responses
})

// Token expiration check utility
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const exp = payload.exp * 1000 // Convert to milliseconds
    return Date.now() >= exp
  } catch {
    return true // If we can't parse, assume expired
  }
}

// Improved token extraction
function getAccessToken(): string | null {
  const token = localStorage.getItem('halcode-auth')
  if (!token) return null
  
  try {
    const parsed = JSON.parse(token)
    // Handle both zustand persist format and direct format
    const accessToken = parsed.state?.accessToken || parsed.accessToken
    
    if (!accessToken) return null
    
    // Check if expired before using
    if (isTokenExpired(accessToken)) {
      console.log('[API] Access token expired, will attempt refresh')
      return null
    }
    
    return accessToken
  } catch (e) {
    console.error('[API] Failed to parse token:', e)
    return null
  }
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken()
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
      console.log('[api interceptor] Authorization header set with valid token')
    } else {
      console.log('[api interceptor] No valid access token found')
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor with better error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const token = localStorage.getItem('halcode-auth')
        if (token) {
          const parsed = JSON.parse(token)
          const refreshToken = parsed.state?.refreshToken || parsed.refreshToken
          
          if (refreshToken) {
            const response = await axios.post('/api/auth/refresh', { refreshToken })
            const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data

            // Update localStorage with proper format
            localStorage.setItem('halcode-auth', JSON.stringify({
              state: { accessToken, refreshToken: newRefreshToken },
              version: 0
            }))

            // Update Authorization header and retry
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
            return api(originalRequest)
          }
        }
      } catch (refreshError) {
        console.error('[api interceptor] Token refresh failed:', refreshError)
        // Clear invalid auth and redirect to login
        localStorage.removeItem('halcode-auth')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle specific error codes
    if (error.response?.status === 401) {
      const errorCode = error.response.data?.code
      
      if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'REFRESH_TOKEN_EXPIRED') {
        localStorage.removeItem('halcode-auth')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Streaming helper function
export async function* makeStreamingRequest(
  data: Record<string, unknown>,
  onChunk: (chunk: string) => void,
  onMetadata?: (metadata: { tokens: number; cost: number; availableTools?: string[]; contextMode?: string }) => void
): AsyncGenerator<string> {
  console.log('[makeStreamingRequest] FUNCTION ENTERED')
  const token = localStorage.getItem('halcode-auth')
  let accessToken = ''
  if (token) {
    try {
      const parsed = JSON.parse(token)
      // Handle zustand persist format: { state: { accessToken, refreshToken }, version: 0 }
      accessToken = parsed.state?.accessToken || parsed.accessToken || ''
    } catch (e) {}
  }

  console.log('[makeStreamingRequest] accessToken:', accessToken ? 'present' : 'missing')
  console.log('[makeStreamingRequest] Calling /api/chat/stream...', data)

  const controller = new AbortController()
  let retryCount = 0
  const maxRetries = 3
  const baseDelay = 1000

  while (retryCount < maxRetries) {
    try {
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      })

      console.log('[makeStreamingRequest] Response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('[makeStreamingRequest] Error:', errorText)
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('Response body is not readable')
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let done = false
      let lastYieldTime = Date.now()
      const MIN_YIELD_INTERVAL = 20 // 20ms for smoother streaming animation

      while (!done) {
        const { done: readerDone, value } = await reader.read()
        if (readerDone) {
          done = true
          break
        }
        
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          // Skip keepalive lines (start with colon)
          if (line.startsWith(':')) {
            continue
          }
          
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6)
            
            if (dataStr === '[DONE]') {
              return
            }
            
            try {
              const parsed = JSON.parse(dataStr)
              
              // Handle different event types
              switch (parsed.type) {
                case 'content':
                  if (parsed.content) {
                    // Enforce 20ms minimum delay between chunk yields for smoother streaming
                    const now = Date.now()
                    const elapsed = now - lastYieldTime
                    if (elapsed < MIN_YIELD_INTERVAL) {
                      // Wait for the remaining time to reach MIN_YIELD_INTERVAL
                      await new Promise(resolve => setTimeout(resolve, MIN_YIELD_INTERVAL - elapsed))
                    }
                    lastYieldTime = Date.now()
                    
                    onChunk(parsed.content)
                    yield parsed.content
                  }
                  break
                case 'metadata':
                  // Handle metadata (tokens, cost, availableTools, contextMode)
                  console.log('Stream metadata:', parsed);
                  if (onMetadata) {
                    onMetadata({
                      tokens: parsed.tokens || 0,
                      cost: parsed.cost || 0,
                      availableTools: parsed.availableTools,
                      contextMode: parsed.contextMode
                    });
                  }
                  break
                case 'done':
                  // Stream complete
                  return
                case 'error':
                  throw new Error(parsed.error || 'Stream error')
                default:
                  // Fallback for backward compatibility
                  if (parsed.content) {
                    // Enforce 20ms minimum delay between chunk yields for smoother streaming
                    const now = Date.now()
                    const elapsed = now - lastYieldTime
                    if (elapsed < MIN_YIELD_INTERVAL) {
                      // Wait for the remaining time to reach MIN_YIELD_INTERVAL
                      await new Promise(resolve => setTimeout(resolve, MIN_YIELD_INTERVAL - elapsed))
                    }
                    lastYieldTime = Date.now()
                    
                    onChunk(parsed.content)
                    yield parsed.content
                  }
              }
            } catch (e) {
              // Skip invalid JSON but log it
              console.warn('Invalid SSE data:', dataStr)
            }
          }
        }
      }
      
      // Success - break retry loop
      break
      
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was aborted - likely cancelled by user
        console.log('Stream aborted by user')
        return
      }
      
      retryCount++
      console.error(`Streaming request failed (attempt ${retryCount}/${maxRetries}):`, error)
      
      if (retryCount < maxRetries) {
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, retryCount - 1)
        console.log(`Retrying in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      } else {
        throw new Error(`Failed after ${maxRetries} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }
  }
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (email: string, password: string, name: string) =>
    api.post('/auth/register', { email, password, name }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get('/auth/me'),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),       
}

// Chat API
export const chatApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/chat', { params }),

  get: (id: string) => api.get(`/chat/${id}`),

  create: (data: { title?: string; workflow?: string; model?: string }) =>    
    api.post('/chat', data),

  update: (id: string, data: { title?: string; workflow?: string; model?: string }) =>
    api.patch(`/chat/${id}`, data),

  delete: (id: string) => api.delete(`/chat/${id}`),

  sendMessage: (data: {
    content: string
    conversationId?: string
    workflow?: string
    model?: string
    attachments?: Array<{ name: string; type: string; content: string }>       
  }) => api.post('/chat/message', data),

  clearAll: () => api.delete('/chat'),
}

// Conversation helpers for chat store
export const conversations = {
  getConversations: async (userId: string) => {
    const response = await api.get<{ data: any[] }>('/chat', {            
      params: { userId }
    })
    return response.data
  },

  createConversation: async (data: { userId: string; title: string; mode: string }) => {
    const response = await api.post<{ data: any }>('/chat', data)      
    return response.data
  },

  deleteConversation: async (id: string) => {
    await api.delete(`/chat/${id}`)
  },

  getConversation: async (id: string) => {
    const response = await api.get<{ data: any }>(`/chat/${id}`)      
    return response.data
  },
}

// Codebase API
export const codebasesApi = {
  list: () => api.get('/codebases'),
  
  get: (id: string) => api.get(`/codebases/${id}`),
  
  getAll: () => api.get('/codebases'),
  
  getFiles: (id: string) => api.get(`/codebases/${id}/files`),
  
  create: (data: { name: string; description?: string; repoUrl?: string }) =>  
    api.post('/codebases', data),
  
  update: (id: string, data: { name?: string; description?: string }) =>       
    api.patch(`/codebases/${id}`, data),
  
  delete: (id: string) => api.delete(`/codebases/${id}`),
  
  search: (id: string, query: string, limit?: number) =>
    api.post(`/codebases/${id}/search`, { query, limit }),
}

// AI API
export const aiApi = {
  getProviders: () => api.get<{ data: AIProviderConfig[] }>('/ai/providers'),
  
  getModels: (provider?: string) => 
    api.get<{ data: AIModel[] }>(`/ai/models${provider ? `/${provider}` : ''}`),
  
  chat: (data: {
    messages: Array<{ role: string; content: string }>
    provider: string
    model: string
    maxTokens?: number
    temperature?: number
  }) => api.post('/ai/chat', data),
  
  chatStream: (data: {
    messages: Array<{ role: string; content: string }>
    provider: string
    model: string
    maxTokens?: number
    temperature?: number
  }) => api.post('/ai/chat/stream', data, { responseType: 'text' }),
  
  getKeys: () => api.get<{ data: Array<{ provider: string; isActive: boolean; lastUsedAt: string }> }>('/ai/keys'),
  
  setKey: (provider: string, apiKey: string) =>
    api.post('/ai/keys', { provider, apiKey }),
  
  deleteKey: (provider: string) =>
    api.delete(`/ai/keys/${provider}`),
}

// Types for AI
export interface AIProviderConfig {
  name: string;
  displayName: string;
  models: AIModel[];
  requiresApiKey: boolean;
  hasApiKey?: boolean;
  supportsStreaming?: boolean;
}

export interface AIModel {
  id: string;
  name: string;
  contextWindow: number;
  maxOutputTokens: number;
  supportsStreaming: boolean;
  available?: boolean;
  pricing: {
    inputPer1kTokens: number;
    outputPer1kTokens: number;
  };
}

// Subscription API
export const subscriptionApi = {
  getCurrent: () => api.get('/subscription'),

  createCheckout: (tier: 'pro' | 'team', priceId?: string) =>
    api.post('/subscription/checkout', { tier, priceId }),

  cancel: () => api.post('/subscription/cancel'),

  portal: () => api.post('/subscription/portal'),
}

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),

  listUsers: (params?: { page?: number; limit?: number; search?: string; tier?: string; role?: string }) =>
    api.get('/admin/users', { params }),

  updateUser: (id: string, data: { role?: string; subscriptionTier?: string }) =>
    api.patch(`/admin/users/${id}`, data),

  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),

  getAnalytics: (period?: string) =>
    api.get('/admin/analytics', { params: { period } }),

  getSubscriptions: () => api.get('/admin/subscriptions'),

  getSettings: () => api.get('/admin/settings'),
}

// Embeddings API
export const embeddingsApi = {
  search: (codebaseId: string, query: string, limit?: number) =>
    api.post('/embeddings/search', { codebaseId, query, limit }),

  findSimilar: (codebaseId: string, filePath: string, limit?: number) =>
    api.post('/embeddings/similar', { codebaseId, filePath, limit }),

  getStats: (codebaseId: string) =>
    api.get(`/embeddings/stats/${codebaseId}`),

  deleteEmbeddings: (codebaseId: string) =>
    api.delete(`/embeddings/codebase/${codebaseId}`),
}

// Agentic API
export const agenticApi = {
  createWorkflow: (data: {
    userRequest: string
    capabilities: string[]
    codebaseId?: string
  }) => api.post('/agentic', data),

  getWorkflows: (limit?: number) =>
    api.get('/agentic', { params: { limit } }),

  getWorkflow: (id: string) =>
    api.get(`/agentic/${id}`),

  regeneratePlan: (id: string) =>
    api.post(`/agentic/${id}/plan`),

  approveWorkflow: (id: string, taskIds?: string[]) =>
    api.post(`/agentic/${id}/approve`, { taskIds }),

  answerQuestion: (workflowId: string, taskId: string, answer: string) =>
    api.post(`/agentic/${workflowId}/tasks/${taskId}/answer`, { answer }),

  skipTask: (workflowId: string, taskId: string, reason?: string) =>
    api.post(`/agentic/${workflowId}/tasks/${taskId}/skip`, { reason }),

  completeWorkflow: (id: string) =>
    api.post(`/agentic/${id}/complete`),

  cancelWorkflow: (id: string) =>
    api.post(`/agentic/${id}/cancel`),

  getChanges: (id: string) =>
    api.get(`/agentic/${id}/changes`),

  getFileDiff: (id: string, path: string) =>
    api.get(`/agentic/${id}/changes/${encodeURIComponent(path)}`),
}

// Multi-Agent API
export const multiAgentApi = {
  getSessions: () => api.get<{ data: any[] }>('/multi-agent'),
  
  getSession: (id: string) => api.get<{ data: any }>(`/multi-agent/${id}`),
  
  createSession: (data: { userRequest: string; vfsId: string; capabilities?: string[]; codebaseId?: string }) =>
    api.post<{ data: any }>('/multi-agent', data),
  
  runArchitect: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/architect`),
  
  approvePlan: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/approve-plan`),
  
  runDeveloper: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/developer`),
  
  runQA: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/qa`),
  
  approveFinal: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/approve-final`),
  
  reject: (sessionId: string, reason: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/reject`, { reason }),
  
  cancel: (sessionId: string) =>
    api.post<{ data: any }>(`/multi-agent/${sessionId}/cancel`),
  
  delete: (sessionId: string) =>
    api.delete(`/multi-agent/${sessionId}`),
}

// GitHub API
export const githubApi = {
  // Get GitHub connection status
  getStatus: () => api.get<{ connected: boolean; username?: string; expiresAt?: string }>('/github/status'),
  
  // Start OAuth flow - returns authorization URL to redirect to
  authorize: () => api.get<{ authUrl: string }>('/github/authorize'),
  
  // Disconnect GitHub account
  disconnect: () => api.post<{ success: boolean; message: string }>('/github/disconnect'),
  
  // List user's GitHub repositories
  getRepos: () => api.get<Array<{
    id: number;
    name: string;
    fullName: string;
    description: string | null;
    private: boolean;
    url: string;
    cloneUrl: string;
    defaultBranch: string;
    updatedAt: string;
  }>>('/github/repos'),
  
  // Clone a repository
  clone: (repoUrl: string, repoName?: string) =>
    api.post<{ success: boolean; message: string; path: string }>('/github/clone', { repoUrl, repoName }),
  
  // List user's locally cloned repositories
  getClonedRepos: () => api.get<Array<{
    name: string;
    path: string;
    branch: string;
    remoteUrl: string;
    lastModified: string;
  }>>('/github/repos/cloned'),
}

// MCP API
export const mcpApi = {
  // Call an MCP tool on a server
  callTool: (serverId: string, toolName: string, params: Record<string, any>) =>
    api.post(`/mcp/servers/${serverId}/tools/${toolName}/call`, params),
  
  // List available tools on a server
  listTools: (serverId: string) =>
    api.get(`/mcp/servers/${serverId}/tools`),
}

// Types for GitHub
export interface GitHubRepo {
  id: number;
  name: string;
  fullName: string;
  description: string | null;
  private: boolean;
  url: string;
  cloneUrl: string;
  defaultBranch: string;
  updatedAt: string;
}

export interface GitHubStatus {
  connected: boolean;
  username?: string;
  expiresAt?: string;
}

export interface ClonedRepo {
  name: string;
  path: string;
  branch: string;
  remoteUrl: string;
  lastModified: string;
}
